import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Play, Cpu, Zap, Target } from 'lucide-react';
export function CapabilitiesVideo() {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          video.play().catch(error => {
            console.log('Autoplay prevented:', error);
          });
        } else {
          video.pause();
        }
      });
    }, {
      threshold: 0.4
    }) // Start playing when 40% of the video is visible
    ;
    observer.observe(video);
    return () => {
      if (video) observer.unobserve(video);
    };
  }, []);
  return <section className="py-24 bg-white" data-id="element-26">
      <div className="container mx-auto px-4 md:px-6" data-id="element-27">
        <div className="max-w-6xl mx-auto" data-id="element-28">
          <div className="text-center mb-12" data-id="element-29">
            <span className="text-accent font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-30">
              See Our Process
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-31">
              Precision Automation in Action
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-32">
              Watch our state-of-the-art robotic systems handle parts with
              precision and consistency, ensuring every component meets exact
              specifications.
            </p>
          </div>

          {/* Video Player */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative aspect-video bg-gray-900 rounded-2xl overflow-hidden shadow-2xl mb-12 border border-gray-100" data-id="element-33">
            <video ref={videoRef} className="w-full h-full object-cover" muted loop playsInline poster="https://cdn.magicpatterns.com/uploads/2BgrBpDJhsdEUMBVmxknzn/Website_Video.undefined" data-id="element-34">
              <source src="https://cdn.magicpatterns.com/uploads/2BgrBpDJhsdEUMBVmxknzn/Website_Video.undefined" type="video/mp4" data-id="element-35" />
              Your browser does not support the video tag.
            </video>

            {/* Optional overlay to ensure text contrast if needed, currently transparent */}
            <div className="absolute inset-0 bg-black/5 pointer-events-none" data-id="element-36"></div>
          </motion.div>

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-37">
            <div className="flex items-start gap-4 p-6 bg-neutral-50 rounded-xl" data-id="element-38">
              <div className="p-3 bg-white rounded-lg text-primary shadow-sm" data-id="element-39">
                <Cpu className="h-6 w-6" data-id="element-40" />
              </div>
              <div data-id="element-41">
                <h3 className="font-bold text-gray-900 mb-1" data-id="element-42">
                  Automated Handling
                </h3>
                <p className="text-sm text-gray-600" data-id="element-43">
                  Robotic systems ensure consistent quality and eliminate human
                  error
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-neutral-50 rounded-xl" data-id="element-44">
              <div className="p-3 bg-white rounded-lg text-primary shadow-sm" data-id="element-45">
                <Zap className="h-6 w-6" data-id="element-46" />
              </div>
              <div data-id="element-47">
                <h3 className="font-bold text-gray-900 mb-1" data-id="element-48">
                  High-Speed Production
                </h3>
                <p className="text-sm text-gray-600" data-id="element-49">
                  Optimized cycle times without compromising precision or
                  quality
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-neutral-50 rounded-xl" data-id="element-50">
              <div className="p-3 bg-white rounded-lg text-primary shadow-sm" data-id="element-51">
                <Target className="h-6 w-6" data-id="element-52" />
              </div>
              <div data-id="element-53">
                <h3 className="font-bold text-gray-900 mb-1" data-id="element-54">
                  Zero Defect Focus
                </h3>
                <p className="text-sm text-gray-600" data-id="element-55">
                  Real-time inspection and quality control at every stage
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}